import React, { useState, useEffect } from 'react';
import { ResourceRequestData, ResourceDetail, ResourceRequestErrors } from '../../types';
import { autoSaveFormData, loadAutoSavedFormData, clearAutoSavedFormData } from '../../utils/storage';

interface ResourceRequestFormProps {
  onSubmit: (data: ResourceRequestData) => void;
  onClear: () => void;
}

const ResourceRequestForm: React.FC<ResourceRequestFormProps> = ({ onSubmit, onClear }) => {
  const [formData, setFormData] = useState<ResourceRequestData>({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    projectTeamName: '',
    numberOfResourcesNeeded: 1,
    resourceDetails: [{
      role: '',
      seniority: '',
      requiredSkills: '',
      quantity: 1
    }],
    contractDuration: '',
    workArrangement: 'remote',
    location: '',
    preferredStartDate: '',
    budgetPerResource: '',
    additionalNotes: ''
  });

  const [errors, setErrors] = useState<ResourceRequestErrors>({});
  const [roleSearchTerm, setRoleSearchTerm] = useState<{[key: number]: string}>({});
  const [customRole, setCustomRole] = useState<{[key: number]: string}>({});

  // Load auto-saved data on component mount
  useEffect(() => {
    const savedData = loadAutoSavedFormData('resource');
    if (savedData) {
      setFormData(savedData);
    }
  }, []);

  // Auto-save form data every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      autoSaveFormData('resource', formData);
    }, 30000);

    return () => clearInterval(interval);
  }, [formData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name as keyof ResourceRequestErrors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleResourceDetailChange = (index: number, field: keyof ResourceDetail, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      resourceDetails: prev.resourceDetails.map((detail, i) => 
        i === index ? { ...detail, [field]: value } : detail
      )
    }));

    // Handle role selection
    if (field === 'role') {
      if (value === 'Other') {
        setCustomRole(prev => ({ ...prev, [index]: '' }));
      }
    }
  };

  const filteredRoleOptions = (index: number) => {
    const searchTerm = roleSearchTerm[index] || '';
    if (!searchTerm) return roleOptions;
    return roleOptions.filter(role => 
      role.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const addResourceDetail = () => {
    setFormData(prev => ({
      ...prev,
      resourceDetails: [...prev.resourceDetails, {
        role: '',
        seniority: '',
        requiredSkills: '',
        quantity: 1
      }]
    }));
  };

  const removeResourceDetail = (index: number) => {
    if (formData.resourceDetails.length > 1) {
      setFormData(prev => ({
        ...prev,
        resourceDetails: prev.resourceDetails.filter((_, i) => i !== index)
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ResourceRequestErrors = {};

    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.projectTeamName.trim()) newErrors.projectTeamName = 'Project/Team name is required';
    if (formData.numberOfResourcesNeeded < 1) newErrors.numberOfResourcesNeeded = 'At least 1 resource is required';
    if (!formData.contractDuration) newErrors.contractDuration = 'Contract duration is required';
    if (!formData.preferredStartDate) newErrors.preferredStartDate = 'Preferred start date is required';

    // Validate location if work arrangement requires it
    if (formData.workArrangement !== 'remote' && !formData.location?.trim()) {
      newErrors.location = 'Location is required for on-site or hybrid work';
    }

    // Validate resource details
    const resourceErrors = formData.resourceDetails.some(detail => 
      !detail.role.trim() || !detail.seniority || !detail.requiredSkills.trim()
    );
    if (resourceErrors) {
      newErrors.resourceDetails = formData.resourceDetails;
    }

    // Email validation (if provided)
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      await onSubmit(formData);
      clearAutoSavedFormData('resource');
      setFormData({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        projectTeamName: '',
        numberOfResourcesNeeded: 1,
        resourceDetails: [{
          role: '',
          seniority: '',
          requiredSkills: '',
          quantity: 1
        }],
        contractDuration: '',
        workArrangement: 'remote',
        location: '',
        preferredStartDate: '',
        budgetPerResource: '',
        additionalNotes: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear the form? This action cannot be undone.')) {
      clearAutoSavedFormData('resource');
      setFormData({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        projectTeamName: '',
        numberOfResourcesNeeded: 1,
        resourceDetails: [{
          role: '',
          seniority: '',
          requiredSkills: '',
          quantity: 1
        }],
        contractDuration: '',
        workArrangement: 'remote',
        location: '',
        preferredStartDate: '',
        budgetPerResource: '',
        additionalNotes: ''
      });
      setErrors({});
      onClear();
    }
  };

  const roleOptions = [
    'Frontend Developer',
    'Backend Developer',
    'Full Stack Developer',
    'Mobile Developer (iOS)',
    'Mobile Developer (Android)',
    'DevOps Engineer',
    'QA Engineer',
    'UI/UX Designer',
    'Project Manager',
    'Tech Lead',
    'Data Engineer',
    'SAP ABAP Developer',
    'SAP Fiori Developer',
    'SAP Basis Administrator',
    'SAP Functional Consultant',
    'SAP Technical Consultant',
    'SAP Security Consultant',
    'SAP Integration Specialist',
    'SAP BW/BI Consultant',
    'SAP HANA Developer',
    'SAP SuccessFactors Consultant',
    'SAP Ariba Consultant',
    'SAP Concur Consultant',
    'SAP Solution Architect',
    'SAP Project Manager',
    'SAP Business Analyst',
    'Other'
  ];

  const seniorityOptions = [
    'Junior (0-2 years)',
    'Mid-level (2-5 years)',
    'Senior (5-8 years)',
    'Lead (8+ years)'
  ];

  const contractDurationOptions = [
    '1-3 months',
    '3-6 months',
    '6-12 months',
    '12+ months',
    'Ongoing'
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Company Information */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-ink-900 mb-4">Company Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-ink-900 mb-2">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-lg bg-surface text-ink-700 focus:outline-none focus:ring-2 transition-colors duration-200 ${
                errors.companyName ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-ink-300 focus:border-brand-start focus:ring-brand-start/20'
              }`}
              placeholder="Enter your company name"
            />
            {errors.companyName && <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>}
          </div>
          <div>
            <label htmlFor="contactPerson" className="block text-sm font-medium text-ink-900 mb-2">
              Contact Person
            </label>
            <input
              type="text"
              id="contactPerson"
              name="contactPerson"
              value={formData.contactPerson}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200"
              placeholder="Your name"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-ink-900 mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-lg bg-surface text-ink-700 focus:outline-none focus:ring-2 transition-colors duration-200 ${
                errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-ink-300 focus:border-brand-start focus:ring-brand-start/20'
              }`}
              placeholder="your.email@company.com"
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-ink-900 mb-2">
              Phone
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200"
              placeholder="+84 123 456 789"
            />
          </div>
        </div>
      </div>

      {/* Project Information */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-ink-900 mb-4">Project Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="projectTeamName" className="block text-sm font-medium text-ink-900 mb-2">
              Project/Team Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="projectTeamName"
              name="projectTeamName"
              value={formData.projectTeamName}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-lg bg-surface text-ink-700 focus:outline-none focus:ring-2 transition-colors duration-200 ${
                errors.projectTeamName ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-ink-300 focus:border-brand-start focus:ring-brand-start/20'
              }`}
              placeholder="Enter project or team name"
            />
            {errors.projectTeamName && <p className="text-red-500 text-sm mt-1">{errors.projectTeamName}</p>}
          </div>
          <div>
            <label htmlFor="numberOfResourcesNeeded" className="block text-sm font-medium text-ink-900 mb-2">
              Number of Resources Needed <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              id="numberOfResourcesNeeded"
              name="numberOfResourcesNeeded"
              min="1"
              max="20"
              value={formData.numberOfResourcesNeeded}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-lg bg-surface text-ink-700 focus:outline-none focus:ring-2 transition-colors duration-200 ${
                errors.numberOfResourcesNeeded ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-ink-300 focus:border-brand-start focus:ring-brand-start/20'
              }`}
            />
            {errors.numberOfResourcesNeeded && <p className="text-red-500 text-sm mt-1">{errors.numberOfResourcesNeeded}</p>}
          </div>
        </div>
      </div>

      {/* Resource Details */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-ink-900 mb-4">Resource Details</h3>
        <div className="space-y-6">
          {formData.resourceDetails.map((detail, index) => (
            <div key={index} className="border border-ink-300 rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-md font-medium text-ink-900">Resource {index + 1}</h4>
                {formData.resourceDetails.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeResourceDetail(index)}
                    className="text-red-500 hover:text-red-700 transition-colors duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink-900 mb-2">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={roleSearchTerm[index] || ''}
                      onChange={(e) => setRoleSearchTerm(prev => ({ ...prev, [index]: e.target.value }))}
                      placeholder="Search roles..."
                      className="w-full px-4 py-2 border border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200 text-sm"
                    />
                    <select
                      value={detail.role}
                      onChange={(e) => handleResourceDetailChange(index, 'role', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200"
                    >
                      <option value="">Select role</option>
                      {filteredRoleOptions(index).map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                    {detail.role === 'Other' && (
                      <input
                        type="text"
                        value={customRole[index] || ''}
                        onChange={(e) => {
                          setCustomRole(prev => ({ ...prev, [index]: e.target.value }));
                          handleResourceDetailChange(index, 'role', e.target.value);
                        }}
                        placeholder="Please specify role"
                        className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200"
                      />
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-900 mb-2">
                    Seniority <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={detail.seniority}
                    onChange={(e) => handleResourceDetailChange(index, 'seniority', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200"
                  >
                    <option value="">Select seniority</option>
                    {seniorityOptions.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-900 mb-2">
                    Required Skills <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={detail.requiredSkills}
                    onChange={(e) => handleResourceDetailChange(index, 'requiredSkills', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200 resize-vertical"
                    placeholder="e.g., React, TypeScript, REST APIs"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-900 mb-2">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={detail.quantity}
                    onChange={(e) => handleResourceDetailChange(index, 'quantity', parseInt(e.target.value) || 1)}
                    className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200"
                  />
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addResourceDetail}
            className="w-full py-3 border-2 border-dashed border-ink-300 rounded-lg text-ink-500 hover:text-brand-start hover:border-brand-start transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Another Resource
          </button>
        </div>
      </div>

      {/* Contract Information */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-ink-900 mb-4">Contract Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="contractDuration" className="block text-sm font-medium text-ink-900 mb-2">
              Contract Duration <span className="text-red-500">*</span>
            </label>
            <select
              id="contractDuration"
              name="contractDuration"
              value={formData.contractDuration}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-lg bg-surface text-ink-700 focus:outline-none focus:ring-2 transition-colors duration-200 ${
                errors.contractDuration ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-ink-300 focus:border-brand-start focus:ring-brand-start/20'
              }`}
            >
              <option value="">Select duration</option>
              {contractDurationOptions.map(duration => (
                <option key={duration} value={duration}>{duration}</option>
              ))}
            </select>
            {errors.contractDuration && <p className="text-red-500 text-sm mt-1">{errors.contractDuration}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-900 mb-2">
              Work Arrangement <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {['remote', 'on-site', 'hybrid'].map(option => (
                <label key={option} className="flex items-center">
                  <input
                    type="radio"
                    name="workArrangement"
                    value={option}
                    checked={formData.workArrangement === option}
                    onChange={handleInputChange}
                    className="mr-3 text-brand-start focus:ring-brand-start"
                  />
                  <span className="text-ink-700 capitalize">{option}</span>
                </label>
              ))}
            </div>
          </div>
          {formData.workArrangement !== 'remote' && (
            <div className="md:col-span-2">
              <label htmlFor="location" className="block text-sm font-medium text-ink-900 mb-2">
                Location <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border-2 rounded-lg bg-surface text-ink-700 focus:outline-none focus:ring-2 transition-colors duration-200 ${
                  errors.location ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-ink-300 focus:border-brand-start focus:ring-brand-start/20'
                }`}
                placeholder="Enter work location"
              />
              {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
            </div>
          )}
          <div>
            <label htmlFor="preferredStartDate" className="block text-sm font-medium text-ink-900 mb-2">
              Preferred Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="preferredStartDate"
              name="preferredStartDate"
              value={formData.preferredStartDate}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-lg text-ink-700 focus:outline-none focus:ring-2 transition-colors duration-200 date-picker-enhanced ${
                errors.preferredStartDate ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-ink-300 focus:border-brand-start focus:ring-brand-start/20'
              }`}
              style={{ 
                backgroundColor: '#1f2937',
                color: '#e5e7eb'
              }}
            />
            {errors.preferredStartDate && <p className="text-red-500 text-sm mt-1">{errors.preferredStartDate}</p>}
          </div>
          <div>
            <label htmlFor="budgetPerResource" className="block text-sm font-medium text-ink-900 mb-2">
              Budget per Resource
            </label>
            <input
              type="text"
              id="budgetPerResource"
              name="budgetPerResource"
              value={formData.budgetPerResource}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200"
              placeholder="e.g., $3,000 - $5,000/month"
            />
          </div>
        </div>
      </div>

      {/* Additional Notes */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-ink-900 mb-4">Additional Notes</h3>
        <div>
          <label htmlFor="additionalNotes" className="block text-sm font-medium text-ink-900 mb-2">
            Additional Notes
          </label>
          <textarea
            id="additionalNotes"
            name="additionalNotes"
            rows={4}
            value={formData.additionalNotes}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200 resize-vertical"
            placeholder="Any additional requirements, constraints, or special considerations"
          />
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-end">
        <button
          type="button"
          onClick={handleClear}
          className="btn-secondary"
        >
          Clear Form
        </button>
        <button
          type="submit"
          className="btn-primary"
        >
          Submit Resource Request
        </button>
      </div>
    </form>
  );
};

export default ResourceRequestForm;
